import {
  Card,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import FormField from "components/FormField/FormField";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { AddJobPhaseCostsInput } from "generated/graphql";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import { CustomJobPhaseCostItem } from "hooks/jobs/job-phases/useGetJobPhaseCostItem";
import { useFieldArray, useFormContext } from "react-hook-form";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { Delete } from "@mui/icons-material";

export default function MaterialNoPlantSection({
  jobPhase,
  costItem,
}: {
  jobPhase: CustomJobPhase;
  costItem: CustomJobPhaseCostItem;
}) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<AddJobPhaseCostsInput>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "materialsNoPlants",
    keyName: "key",
  });

  const materialsNoPlants = watch("materialsNoPlants");

  const totalCost =
    materialsNoPlants?.reduce((sum, material) => {
      const quantity = parseFloat(material?.quantity?.toString() || "0");
      const rate = parseFloat(material?.rate?.toString() || "0");
      return sum + quantity * rate;
    }, 0) ?? 0;

  return (
    <Card>
      <MDBox sx={{ p: 4, overflow: "auto", maxWidth: "100%" }}>
        <MDTypography variant="h6" mb={2}>
          Job Costs - {jobPhase?.organizationProductTypeName}
        </MDTypography>
        {errors.materialsNoPlants?.message && (
          <MDBox my={2}>
            <FormHelperText error>{errors.materialsNoPlants.message}</FormHelperText>
          </MDBox>
        )}
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell sx={{ minWidth: "200px", width: "30%" }}>Material Name</TableCell>
              <TableCell sx={{ minWidth: "150px", width: "20%" }} align="right">
                Quantity
              </TableCell>
              <TableCell sx={{ minWidth: "150px", width: "20%" }} align="right">
                Rate
              </TableCell>
              <TableCell sx={{ minWidth: "150px", width: "20%" }} align="right">
                Total
              </TableCell>
              <TableCell sx={{ minWidth: "50px", width: "10%" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields?.map((material, index) => {
              const watchedMaterial = materialsNoPlants?.[index];
              const quantity = parseFloat(watchedMaterial?.quantity?.toString() || "0");
              const rate = parseFloat(watchedMaterial?.rate?.toString() || "0");
              const rowTotal = quantity * rate;

              return (
                <TableRow
                  key={material.key}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <FormField
                      type="text"
                      placeholder="Material name"
                      mb={0}
                      {...control.register(`materialsNoPlants.${index}.name`)}
                      error={errors.materialsNoPlants?.[index]?.name}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <FormField
                      type="number"
                      inputProps={{ step: "0.01", min: 0 }}
                      placeholder="0"
                      mb={0}
                      sx={{
                        "& .MuiInputBase-root": {
                          "& input": {
                            textAlign: "right",
                          },
                        },
                      }}
                      {...control.register(`materialsNoPlants.${index}.quantity`)}
                      error={errors.materialsNoPlants?.[index]?.quantity}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <CurrencyFormField
                      control={control}
                      name={`materialsNoPlants.${index}.rate`}
                      label=""
                      inputProps={{ min: 0 }}
                      sendEmptyValuesAsNull={false}
                      mb={0}
                      sx={{
                        "& .MuiInputBase-root": {
                          "& input": {
                            textAlign: "right",
                          },
                        },
                      }}
                      error={errors.materialsNoPlants?.[index]?.rate}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography variant="body2">{formatCentsToUSD(rowTotal)}</MDTypography>
                  </TableCell>
                  <TableCell align="center">
                    <MDButton variant="text" color="error" onClick={() => remove(index)}>
                      <Delete />
                    </MDButton>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
              <TableCell colSpan={5}>
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={() =>
                    append({
                      name: "",
                      quantity: 0,
                      rate: "0",
                    })
                  }
                >
                  Add Material
                </MDButton>
              </TableCell>
            </TableRow>
            {fields && fields.length > 0 && (
              <TableRow sx={{ borderTop: 2, borderColor: "divider" }}>
                <TableCell colSpan={3} align="right">
                  <MDTypography variant="h6">Total Material Cost:</MDTypography>
                </TableCell>
                <TableCell align="right">
                  <MDTypography variant="h6">{formatCentsToUSD(totalCost)}</MDTypography>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </MDBox>
    </Card>
  );
}
